// frontend/src/components/current-load/CurrentLoadWorkspace.jsx

import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Truck } from "lucide-react";
import { FaCheckCircle } from "react-icons/fa";

import CurrentLoadRoutePanel from "./CurrentLoadRoutePanel.jsx";
import CurrentLoadDetailsPanel from "./CurrentLoadDetailsPanel.jsx";
import CurrentLoadNotesPanel from "./CurrentLoadNotesPanel.jsx";
import CurrentLoadMap from "./CurrentLoadMap.jsx";

import PendentLoadAuditPanel from "../pendent-load/PendentLoadAuditPanel.jsx";

import "../../styles/EditShipmentRequestPage.css";
import "../../styles/EditPendentLoadWorkspace.css";

function getFirstPointName(points, fallback) {
  const point = (Array.isArray(points) ? points : []).find((item) =>
    String(item?.location || "").trim()
  );

  return String(point?.location || fallback).trim();
}

function getLastPointName(points, fallback) {
  const safePoints = Array.isArray(points) ? points : [];

  for (let index = safePoints.length - 1; index >= 0; index -= 1) {
    const location = String(safePoints[index]?.location || "").trim();

    if (location) {
      return location;
    }
  }

  return fallback;
}

export default function CurrentLoadWorkspace(props) {
  const { t } = useTranslation("edit_load");

  const {
    ready,
    fetching,
    saving,
    error,
    successPopupOpen,
    onCloseSuccessPopup,
    onBack,
    onSave,
    load,
    loadNumber,
    transportSessionNumber,
    pickupPoints,
    deliveryPoints,
    weight,
    height,
    trailerType,
    merchandise,
  } = props;

  const currentTransportSession =
    transportSessionNumber ??
    load?.transportSession?.transportSessionNumber ??
    load?.transportSessionNumber ??
    load?.tsUiCounter ??
    load?.transportsessionId ??
    load?.transportSessionId ??
    "—";

  const currentLoadNumber =
    loadNumber ??
    load?.loadNumber ??
    load?.uiCounter ??
    load?.id ??
    "—";

  const loadSourceType =
    String(
      load?.sourceType ??
      load?.originType ??
      load?.loadSource ??
      ""
    )
      .trim()
      .toUpperCase();

  const isExplicitShipmentRequestSource =
    [
      "SHIPMENT_REQUEST",
      "SHIPPER_REQUEST",
      "SHIPMENTREQUEST",
    ].includes(
      loadSourceType
    );

  const rawShipmentRequestId =
    load?.shipmentRequestId ??
    load?.shipmentRequest?.id ??
    load?.sourceShipmentRequestId ??
    (
      isExplicitShipmentRequestSource
        ? load?.requestId
        : null
    );

  const numericShipmentRequestId =
    Number(
      rawShipmentRequestId
    );

  const shipmentRequestId =
    Number.isInteger(
      numericShipmentRequestId
    ) &&
    numericShipmentRequestId >
      0
      ? numericShipmentRequestId
      : null;

  const isShipmentRequestLoad =
    Boolean(
      shipmentRequestId
    );

  const firstPickupName = getFirstPointName(
    pickupPoints,
    t("workspace.origin", {
      defaultValue: "Origem",
    })
  );

  const lastDeliveryName = getLastPointName(
    deliveryPoints,
    t("workspace.destination", {
      defaultValue: "Destino",
    })
  );

  if (!ready) {
    return (
      <div className="active-shipment-page-v2 edit-pendent-load-page-v2 current-load-page-v2">
        <div className="active-shipment-shell-v2">
          <div className="edit-pendent-load-loading-state">
            <Truck size={18} />

            <span>
              {fetching
                ? t("status.loading")
                : t("status.loading")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="active-shipment-page-v2 edit-pendent-load-page-v2 current-load-page-v2">
      <div className="active-shipment-shell-v2">
        {successPopupOpen && (
          <div
            className="edit-pendent-load-popupOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="current-load-success-title"
          >
            <div className="edit-pendent-load-popupModal">
              <div className="edit-pendent-load-popupIconWrap">
                <FaCheckCircle className="edit-pendent-load-popupIcon" />
              </div>

              <h2
                id="current-load-success-title"
                className="edit-pendent-load-popupTitle"
              >
                {t("popup.updated_title")}
              </h2>

              <p className="edit-pendent-load-popupMessage">
                {t("popup.updated_message")}
              </p>

              <div className="edit-pendent-load-popupActions">
                <button
                  type="button"
                  className="edit-pendent-load-popupPrimaryBtn"
                  onClick={onCloseSuccessPopup}
                >
                  {t("popup.ok")}
                </button>
              </div>
            </div>
          </div>
        )}

<header className="shipment-v2-header edit-pendent-load-header-v2 current-load-header-v2">
          <div className="shipment-v2-header-left">
            <button
              type="button"
              className="shipment-v2-back-btn"
              onClick={onBack}
            >
              <ArrowLeft size={15} />

              {t("workspace.backToCurrentLoads", {
                defaultValue: "Voltar às cargas",
              })}
            </button>

            <div className="shipment-v2-title-row pendent-workspace-title-row">
              <h1>
                {firstPickupName}

                <span className="pendent-workspace-title-arrow">
                  →
                </span>

                {lastDeliveryName}

                <span className="pendent-workspace-title-reference current-load-title-reference">
                  {t("workspace.currentLoadReference", {
                    defaultValue:
                      "LOAD #{{load}} · TS #{{session}}",

                    load: currentLoadNumber,
                    session: currentTransportSession,
                  })}
                </span>
              </h1>

              <span className="shipment-v2-divider" />

              <span className="shipment-v2-status-pill pendent-workspace-status-pill">
                <Truck size={13} />

                {t("workspace.currentLoadStatus", {
                  defaultValue: "Atual",
                })}
              </span>
            </div>

            <div className="shipment-v2-route-summary">
              <span>
                {firstPickupName}
              </span>

              <span>
                →
              </span>

              <span>
                {lastDeliveryName}
              </span>
            </div>
          </div>

          <div className="current-load-header-actions">
            <button
              type="button"
              className="current-load-header-cancel-btn"
              disabled={saving}
              onClick={onBack}
            >
              {t("buttons.cancel")}
            </button>

            <button
              type="submit"
              form="current-load-workspace-form"
              className="current-load-header-save-btn"
              disabled={saving}
            >
              <FaCheckCircle size={14} />

              {saving
                ? t("buttons.updating")
                : t("buttons.update")}
            </button>
          </div>
        </header>

        <CurrentLoadMap
          pickupPoints={pickupPoints}
          deliveryPoints={deliveryPoints}
          weight={weight}
          height={height}
          trailerType={trailerType}
        />

        <form
          id="current-load-workspace-form"
          className="current-load-workspace-form"
          onSubmit={onSave}
        >
          <main className="shipment-v2-main-grid pendent-workspace-main-grid current-load-workspace-main-grid">
            <CurrentLoadRoutePanel
              {...props}
            />

            <CurrentLoadDetailsPanel
              {...props}
              isShipmentRequestLoad={
                isShipmentRequestLoad
              }
            />

            {isShipmentRequestLoad ? (
              <PendentLoadAuditPanel
                requestId={
                  shipmentRequestId
                }
                pendentLoadNumber={
                  currentLoadNumber
                }
                pickupCount={
                  Array.isArray(
                    pickupPoints
                  )
                    ? pickupPoints.length
                    : 0
                }
                deliveryCount={
                  Array.isArray(
                    deliveryPoints
                  )
                    ? deliveryPoints.length
                    : 0
                }
                merchandise={
                  merchandise
                }
                currentPendentLoad={{
                  ...load,

                  shipperCompanyName:
                    load?.shipperCompanyName ??
                    load?.shipmentRequest
                      ?.shipperCompanyName ??
                    load?.industryClientName ??
                    load?.industryClient
                      ?.companyName ??
                    load?.clientCompanyName ??
                    "",
                }}
                showAudit={
                  false
                }
              />
            ) : (
              <CurrentLoadNotesPanel
                {...props}
                currentTransportSession={currentTransportSession}
                currentLoadNumber={currentLoadNumber}
              />
            )}
          </main>

          {error && (
            <div className="edit-pendent-load-page-error current-load-page-error">
              {error}
            </div>
          )}

          {error && (
            <div className="edit-pendent-load-page-error current-load-page-error">
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}